/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { stateToHTML } from 'draft-js-export-html';

/**
 * Internal dependencies
 */
import { getExplicitEmptyLineHeight } from './emptyLineHeight';
import compactListMarkup from '../../utils/compactListMarkup';
import { LIST_BLOCK_TYPES } from './constants';
import formatters from './formatters';

function inlineStyleFn(context) {
  return (styles) => {
    const { style: inlineCSS, attributes } = formatters.reduce(
      (result, { stylesToCSS, stylesToAttributes }) => ({
        style: {
          ...result.style,
          ...(stylesToCSS?.(styles, context) || {}),
        },
        attributes: {
          ...result.attributes,
          ...(stylesToAttributes?.(styles, context) || {}),
        },
      }),
      { style: {}, attributes: {} }
    );

    if (
      Object.keys(inlineCSS).length === 0 &&
      Object.keys(attributes).length === 0
    ) {
      return null;
    }

    return {
      element: 'span',
      style: inlineCSS,
      attributes,
    };
  };
}

function exportHTML(editorState, context = {}) {
  if (!editorState) {
    return null;
  }

  const content = editorState.getCurrentContent();
  const hasList = content
    .getBlockMap()
    .some((block) => LIST_BLOCK_TYPES.includes(block.getType()));
  const html = stateToHTML(content, {
    inlineStyleFn: inlineStyleFn(context),
    blockRenderers: {
      unstyled: (block) => {
        const height = getExplicitEmptyLineHeight(block);
        if (height === null) {
          return null;
        }
        const marker = `<span data-st-line-height="${height}"></span>`;
        return hasList ? `<p>${marker}</p>` : marker;
      },
    },
    // Anonymous root text is reordered ahead of lists by draft-js-import-html.
    // Keep legacy flat content unchanged, but give paragraphs stable block
    // boundaries whenever they share a text box with semantic lists.
    defaultBlockTag: hasList ? 'p' : null,
  });

  if (!hasList) {
    return html.replace(/<br ?\/?>/g, '').replace(/&nbsp;$/, '');
  }

  const lines = html.split('\n');
  const normalizedHtml = lines
    .join('\n')
    .replace(/\n$/, '')
    .replace(/&nbsp;$/, '');

  return compactListMarkup(normalizedHtml);
}

export default exportHTML;
